import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { AuthContext } from './AuthContext';
import { API_URL } from '../config';

export const SocketContext = createContext();

export const SocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [newMediaNotification, setNewMediaNotification] = useState(null);
  const [mediaViewedNotification, setMediaViewedNotification] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (user && !socketRef.current) {
      let token = null;
      try {
        token = localStorage.getItem('token');
      } catch (e) {
        console.warn('localStorage error:', e.message);
      }

      console.log('🔌 Connexion au serveur socket...');
      socketRef.current = io(API_URL, {
        auth: { token }
      });

      const socket = socketRef.current;

      const handleConnect = () => {
        console.log('✅ Socket connecté:', socket.id);
        setIsConnected(true);
        // Demander la liste des utilisateurs en ligne
        socket.emit('request-online-users');
      };

      const handleOnlineUsersList = (users) => {
        console.log('📋 Liste des utilisateurs en ligne:', users);
        setOnlineUsers(users);
      };

      const handleUserOnline = (email) => {
        console.log('🟢 Utilisateur en ligne:', email);
        setOnlineUsers(prev => prev.includes(email) ? prev : [...prev, email]);
      };

      const handleUserOffline = (email) => {
        console.log('🔴 Utilisateur hors ligne:', email);
        setOnlineUsers(prev => prev.filter(e => e !== email));
      };

      const handleDisconnect = () => {
        console.log('❌ Socket déconnecté');
        setIsConnected(false);
      };

      const handleIncomingCall = (data) => {
        console.log('📞 Appel entrant reçu:', data);
        setIncomingCall(data);
      };

      const handleCallDeclined = () => {
        console.log('❌ Appel refusé');
        // Toast géré par SocketToastHandler dans App.js
      };

      const handleUserUnavailable = (data) => {
        console.log('⚠️ Utilisateur non disponible:', data.targetEmail);
        // Toast géré par SocketToastHandler dans App.js
      };

      const handleCallEnded = () => {
        console.log('🔴 Appel terminé par l\'autre partie');
      };

      const handleAnsweredElsewhere = () => {
        console.log('📱 Appel décroché sur un autre appareil');
        setIncomingCall(null);
      };

      const handleNewMediaNotification = (data) => {
        console.log('📸 Nouveaux médias reçus:', data);
        setNewMediaNotification(data);

        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try {
            new Notification('Nouveaux médias reçus !', {
              body: `${data.senderEmail} vous a envoyé ${data.count} fichier(s)`,
              icon: '/logo192.png'
            });
          } catch (e) {
            console.warn('Notification API error:', e.message);
          }
        }
      };

      const handleMediaViewedNotification = (data) => {
        console.log('👁️ Média consulté:', data);
        setMediaViewedNotification(data);

        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try {
            new Notification('Média consulté', {
              body: `${data.recipientEmail} a consulté votre fichier "${data.originalName}"`,
              icon: '/logo192.png'
            });
          } catch (e) {
            console.warn('Notification API error:', e.message);
          }
        }
      };

      // Attacher tous les listeners
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
      socket.on('incoming-call', handleIncomingCall);
      socket.on('call-declined', handleCallDeclined);
      socket.on('user-unavailable', handleUserUnavailable);
      socket.on('call-ended', handleCallEnded);
      socket.on('new-media-notification', handleNewMediaNotification);
      socket.on('media-viewed-notification', handleMediaViewedNotification);
      socket.on('online-users-list', handleOnlineUsersList);
      socket.on('user-online', handleUserOnline);
      socket.on('user-offline', handleUserOffline);
      socket.on('answered-elsewhere', handleAnsweredElsewhere);

      // Cleanup: retirer tous les listeners à la déconnexion
      return () => {
        console.log('🧹 Nettoyage des listeners socket');
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
        socket.off('incoming-call', handleIncomingCall);
        socket.off('call-declined', handleCallDeclined);
        socket.off('user-unavailable', handleUserUnavailable);
        socket.off('call-ended', handleCallEnded);
        socket.off('new-media-notification', handleNewMediaNotification);
        socket.off('media-viewed-notification', handleMediaViewedNotification);
        socket.off('online-users-list', handleOnlineUsersList);
        socket.off('user-online', handleUserOnline);
        socket.off('user-offline', handleUserOffline);
        socket.off('answered-elsewhere', handleAnsweredElsewhere);
      };
    }

    if (!user && socketRef.current) {
      console.log('👋 Déconnexion du socket');
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      setIncomingCall(null);
      setNewMediaNotification(null);
      setMediaViewedNotification(null);
      setOnlineUsers([]);
    }
  }, [user]);

  // Demander la permission pour les notifications
  useEffect(() => {
    if (user && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(err => console.warn('Notification permission error:', err.message));
    }
  }, [user]);

  const clearIncomingCall = () => {
    setIncomingCall(null);
  };

  const clearMediaNotification = () => {
    setNewMediaNotification(null);
  };

  const clearViewedNotification = () => {
    setMediaViewedNotification(null);
  };

  return (
    <SocketContext.Provider
      value={{
        socket: socketRef.current,
        isConnected,
        incomingCall,
        clearIncomingCall,
        newMediaNotification,
        clearMediaNotification,
        mediaViewedNotification,
        clearViewedNotification,
        onlineUsers
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export default SocketProvider;